import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { Button } from "src/primitives/Button";
import { cn } from "src/shadcn/lib/utils";

import { decadeStartFor } from "./utils";

interface YearPickerProps {
  /** Currently selected date (used to highlight the active year). */
  value: Date | null;
  /** A year inside the decade currently shown in the grid. */
  displayedYear: number;
  /** Callback when the user navigates to a different decade (receives any year inside it). */
  onYearChange: (year: number) => void;
  /** Callback when a year is picked. */
  onSelect: (date: Date) => void;
  /** Predicate to disable an entire year (e.g. when out of min/max range). */
  isDisabled?: (date: Date) => boolean;
}

const YearPicker = ({
  value,
  displayedYear,
  onYearChange,
  onSelect,
  isDisabled,
}: YearPickerProps) => {
  const decadeStart = decadeStartFor(displayedYear);
  const years = Array.from({ length: 12 }, (_, i) => decadeStart - 1 + i);
  const selectedYear = value?.getFullYear();

  return (
    <div className="bg-background flex w-[16rem] flex-col gap-3 p-2">
      <div className="flex items-center justify-between">
        <Button
          aria-label="Previous decade"
          size="icon"
          variant="ghost"
          onClick={() => onYearChange(decadeStart - 10)}
        >
          <ChevronLeftIcon className="size-4 rtl:rotate-180" />
        </Button>
        <span className="text-sm font-medium">
          {decadeStart}-{decadeStart + 9}
        </span>
        <Button
          aria-label="Next decade"
          size="icon"
          variant="ghost"
          onClick={() => onYearChange(decadeStart + 10)}
        >
          <ChevronRightIcon className="size-4 rtl:rotate-180" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {years.map(year => {
          const yearDate = new Date(year, 0, 1);
          const disabled = isDisabled?.(yearDate) ?? false;
          const selected = selectedYear === year;
          const outsideDecade = year < decadeStart || year > decadeStart + 9;

          return (
            <button
              aria-pressed={selected}
              className={cn(
                "rounded-md py-2 text-sm transition-colors",
                "hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
                outsideDecade && "text-muted-foreground",
                selected &&
                  "bg-primary text-primary-foreground hover:bg-primary"
              )}
              disabled={disabled}
              key={year}
              type="button"
              onClick={() => onSelect(yearDate)}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { YearPicker };
